import {
  AfterContentInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { BaseService } from 'src/app/tabms/services/base/base.service';
import { FieldsConfigService } from 'src/app/tabms/services/global/fields-config.service';
import { FieldDictionaryListService } from 'src/app/tabms/services/global/field-dictionary-list.service';
import { PermissionsListService } from 'src/app/tabms/services/global/permissions-list.service';
import { SessionWrapperService } from 'src/app/tabms/services/session/session-wrapper.service';
import { ImageHelperService } from 'src/app/tabms/services/global/image-helper.service';
import { DynamicSetFormValidatorsService } from 'src/app/tabms/services/global/dynamic-set-form-validators.service';
import { IActualSystemMenuPermission, LocalFormData, SingleResponseModel } from 'src/app/tabms/models/base/base';
import { EDITSUCCESS, OPERERROR, SAVESUCCESS } from 'src/app/tabms/tab-consts';
import { validate } from 'src/app/tabms/shared/utils/form-validation.utils';

/**
 * Supported view modes for the base-entry-views component
 */
export type ViewMode = 'grid' | 'cards' | 'map';

/**
 * Configuration for a single view mode
 */
export interface ViewModeConfig {
  mode: ViewMode;
  icon: string;
  labelKey: string;  // Translation key for the view label
}

/**
 * Default view mode configurations
 */
export const DEFAULT_VIEW_CONFIGS: ViewModeConfig[] = [
  { mode: 'grid', icon: 'view_list', labelKey: 'gridView' },
  { mode: 'cards', icon: 'apps', labelKey: 'cardsView' },
  { mode: 'map', icon: 'place', labelKey: 'mapView' }
];

/**
 * BaseEntryViewsComponent - A multi-view extension of the base-entry component
 *
 * This component provides the same CRUD functionality as base-entry but adds
 * support for multiple view modes (grid, cards, map) with seamless switching.
 *
 * Usage:
 * ```html
 * <app-base-entry-views [service]="myService" [views]="['grid', 'cards', 'map']">
 *   <app-my-criteria #criteriaComponent criteria></app-my-criteria>
 *   <app-my-form #formComponent form></app-my-form>
 *
 *   <!-- Card template for cards view -->
 *   <ng-template #cardTemplate let-item>
 *     <app-my-card [data]="item"></app-my-card>
 *   </ng-template>
 *
 *   <!-- Map component for map view -->
 *   <ng-template #mapTemplate let-data="data">
 *     <app-my-map [items]="data"></app-my-map>
 *   </ng-template>
 * </app-base-entry-views>
 * ```
 */
@Component({
  selector: 'app-base-entry-views',
  templateUrl: './base-entry-views.component.html',
  styleUrl: './base-entry-views.component.scss'
})
export class BaseEntryViewsComponent<T> implements OnInit, AfterContentInit, OnDestroy {
  //#region Inputs - Service & Permissions (same as base-entry)
  @Input() set service(_service) {
    if (!_service) return;
    this.serviceInstance = _service;
  }

  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;
  @Input() canCreate: boolean = true;

  @Input() disableActionsFieldName?: string;
  @Input() disableActionsValue?: any;
  @Input() disableDeleteFieldName?: string;
  @Input() disableDeleteValue?: any;
  //#endregion

  //#region Inputs - View Configuration
  /**
   * Array of view modes to enable. Example: ['grid', 'cards', 'map']
   */
  @Input() views: ViewMode[] = ['grid'];

  /**
   * The initial/default view mode
   */
  @Input() defaultView: ViewMode = 'grid';

  /**
   * Custom view configurations (override icons, labels)
   */
  @Input() viewConfigs: ViewModeConfig[] = DEFAULT_VIEW_CONFIGS;

  /**
   * Storage key for persisting view preference in localStorage
   */
  @Input() viewStorageKey: string | null = null;
  //#endregion

  //#region Content Children - Templates
  @ContentChild('formComponent') formComponent;
  @ContentChild('cardTemplate') cardTemplate: TemplateRef<any>;
  @ContentChild('mapTemplate') mapTemplate: TemplateRef<any>;
  //#endregion

  //#region Outputs
  @Output() viewModeChange = new EventEmitter<ViewMode>();
  @Output() dataLoaded = new EventEmitter<T[]>();
  //#endregion

  //#region Public Properties
  serviceInstance;
  currentView: ViewMode = 'grid';
  showCreate = false;
  editId;
  form: FormGroup;
  submited = false;
  permissions: IActualSystemMenuPermission;
  systemMenuId: string;
  initialValues;
  loading = false;

  // Data for views
  gridData: T[] = [];
  totalCount = 0;
  //#endregion

  //#region Private Properties
  private destroy$: Subject<void> = new Subject<void>();
  private keepOpenOnNextSave = false;
  private criteriaParams: any = {};
  private menuKey: string = '';
  private pageParameter: any = null;
  //#endregion

  //#region Constructor
  constructor(
    private baseService: BaseService,
    public fieldsConfigService: FieldsConfigService<T>,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private fieldDictionaryListService: FieldDictionaryListService,
    private permissionListService: PermissionsListService,
    private sessionService: SessionWrapperService,
    private imageHelper: ImageHelperService,
    private dynamicSetFormValidation: DynamicSetFormValidatorsService
  ) {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.systemMenuId = params['id'];
      this.fieldsConfigService.setMenuId(this.systemMenuId);
      this.fieldDictionaryListService.getPageFields(this.systemMenuId);
      this.permissionListService.getPage(this.systemMenuId);
    });

    this.fieldsConfigService.getData();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    // Initialize view from storage or default
    this.initializeView();

    // Subscribe to permissions
    this.permissionListService
      .getPagePermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) {
            this.permissions = res;
          }
        }
      });
  }

  ngAfterContentInit(): void {
    this.baseService.formInitialized.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (this.formComponent?.form) {
        this.form = this.formComponent.form;
        this.initialValues = this.form.getRawValue();
        this.initializationCode();
      }
    });

    // Subscribe to criteria changes
    this.baseService.applyCriteria.pipe(takeUntil(this.destroy$)).subscribe((value: Object) => {
      this.criteriaParams = value;
      if (this.currentView !== 'grid') {
        this.loadDataForView();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.baseService.createFormDisplay.next(null);
    this.fieldsConfigService.setMenuId('');
  }
  //#endregion

  //#region View Mode Methods
  /**
   * Get the available view configurations based on enabled views
   */
  getAvailableViews(): ViewModeConfig[] {
    return this.viewConfigs.filter(config => this.views.includes(config.mode));
  }

  /**
   * Check if a specific view mode is enabled
   */
  isViewEnabled(mode: ViewMode): boolean {
    return this.views.includes(mode);
  }

  /**
   * Switch to a different view mode
   */
  setViewMode(mode: ViewMode): void {
    if (!this.isViewEnabled(mode)) return;

    this.currentView = mode;
    this.viewModeChange.emit(mode);

    // Persist preference
    if (this.viewStorageKey) {
      try {
        localStorage.setItem(this.viewStorageKey, mode);
      } catch {
        // Ignore storage errors
      }
    }

    // Load data for the new view if needed
    if (mode !== 'grid') {
      this.loadDataForView();
    }
  }

  /**
   * Initialize view mode from storage or default
   */
  private initializeView(): void {
    let savedView: ViewMode | null = null;

    if (this.viewStorageKey) {
      try {
        savedView = localStorage.getItem(this.viewStorageKey) as ViewMode;
      } catch {
        // Ignore storage errors
      }
    }

    if (savedView && this.isViewEnabled(savedView)) {
      this.currentView = savedView;
    } else if (this.isViewEnabled(this.defaultView)) {
      this.currentView = this.defaultView;
    } else {
      this.currentView = this.views[0] || 'grid';
    }
  }

  /**
   * Load all data for non-grid views (cards, map)
   * Respects criteria and filters just like the grid view
   */
  loadDataForView(): void {
    if (!this.serviceInstance) return;

    this.loading = true;

    // Use gridList with criteria params if available (same as base-grid does)
    const hasParams = this.criteriaParams && Object.keys(this.criteriaParams).length > 0;
    const serviceCall = hasParams && this.serviceInstance.gridList
      ? this.serviceInstance.gridList(this.menuKey || this.systemMenuId, this.pageParameter, 1, 0, 100, this.criteriaParams)
      : this.serviceInstance.getAll();

    serviceCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const rawItems = res?.result?.items || res?.result || res?.items || [];

        // Process items same as base-grid does - extract and clean _entitydto
        this.gridData = rawItems.map(item => {
          const entity = item._entitydto ?? {};

          // Transform keys: replace dots with underscores to match column definitions
          const cleanEntity = Object.fromEntries(
            Object.entries(entity).map(([key, value]) => [
              key.replace(/\./g, '_'),
              value
            ])
          );

          // Return the cleaned entity as the item
          return {
            ...item,
            ...cleanEntity
          };
        });

        this.totalCount = res?.result?.totalcount || rawItems.length;
        this.dataLoaded.emit(this.gridData);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }
  //#endregion

  //#region Initialization (same as base-entry)
  private initializationCode(): void {
    // Show/hide create form and receive ID for edit
    this.baseService.createFormDisplay.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res) return;
      this.showCreate = res?.show;
      this.editId = res?.id;

      this.dynamicSetFormValidation.setValidators(this.form, this.systemMenuId, this.destroy$, !this.editId);
      if (this.editId) {
        this.getById(this.editId);
      }
      this.baseService.sendEditId.next(res?.id);
    });

    // Save action
    this.baseService.saveForm.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.onSubmit();
    });

    // Clear action
    this.baseService.clearForm.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.form.reset();
      this.form.patchValue(this.initialValues);

      if (this.formComponent && typeof (this.formComponent as any).resetImageState === 'function') {
        (this.formComponent as any).resetImageState();
      }
      if (this.formComponent && typeof (this.formComponent as any).resetFileState === 'function') {
        (this.formComponent as any).resetFileState();
      }
    });

    // Local data save/restore
    this.baseService.saveRetrieveLocalData.pipe(takeUntil(this.destroy$)).subscribe((saveorRetrieve: number) => {
      if (saveorRetrieve == 1) {
        this.saveLocalData();
      } else if (saveorRetrieve == 2) {
        this.retrieveLocalData();
      }
    });

    // Keep form open on save
    this.baseService.keepFormOpenOnSave
      .pipe(takeUntil(this.destroy$))
      .subscribe((keep) => {
        this.keepOpenOnNextSave = !!keep;
      });

    // Refresh table subscription
    this.baseService.refreshTable.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      if (value && this.currentView !== 'grid') {
        this.loadDataForView();
      }
    });
  }

  cancelForm(): void {
    this.form.reset();
    this.form.patchValue(this.initialValues);
    this.submited = false;
    this.baseService.createFormDisplay.next({ show: false, id: null });
  }
  //#endregion

  //#region CRUD Operations (same as base-entry)
  onSubmit(): void {
    this.submited = true;
    if (this.formComponent.createUserCheck) {
      this.add();
    } else {
      if (validate(this.form)) {
        if (!this.editId) this.add();
        else this.update();
      }
    }
  }

  async add(): Promise<void> {
    const asyncBeforeSaveWork = this.formComponent.beforeSaveWork_async
      ? await this.formComponent.beforeSaveWork_async()
      : true;
    const syncBeforeSaveWork = this.formComponent.beforeSaveWork
      ? this.formComponent.beforeSaveWork()
      : true;

    if (syncBeforeSaveWork && asyncBeforeSaveWork) {
      let formData: T = this.form.getRawValue();

      Object.keys(formData).forEach((key) => {
        if (key.toLowerCase() === 'branchid' && formData[key] === null) {
          formData[key] = this.sessionService.DefaultBranchID;
        }
      });

      if (this.formComponent.hasImage) {
        formData = await this.imageHelper.formHasImage(formData, this.destroy$);
      }

      this.serviceInstance.create(formData).pipe(takeUntil(this.destroy$)).subscribe(
        async (res) => {
          let id = res?.result == null ? res.id : res.result.id;
          this.baseService.showSuccessMessage(SAVESUCCESS);
          this.removeLocalStorage();

          if (this.formComponent.afterSaveWork) {
            this.formComponent.afterSaveWork(id);
          } else if (this.formComponent.afterSaveWork_async) {
            await this.formComponent.afterSaveWork_async(id);
          }

          if (this.keepOpenOnNextSave) {
            this.baseService.createFormDisplay.next({ show: true, id: '' });
            this.keepOpenOnNextSave = false;
          } else {
            this.cancelForm();
          }
          this.baseService.refreshTable.next(true);
          this.serviceInstance.channel.postMessage('new-item-added');
        },
        (err) => {
          console.log('err', err);
          this.baseService.showErrorMessage(OPERERROR);
        }
      );
    }
  }

  async update(): Promise<void> {
    const asyncBeforeUpdateWork = this.formComponent.beforeUpdateWork_async
      ? await this.formComponent.beforeUpdateWork_async()
      : true;
    const syncBeforeUpdateWork = this.formComponent.beforeUpdateWork
      ? this.formComponent.beforeUpdateWork()
      : true;

    if (syncBeforeUpdateWork && asyncBeforeUpdateWork) {
      let formData: T = { ...this.form.getRawValue(), id: this.editId };

      if (this.formComponent.hasImage) {
        formData = await this.imageHelper.formHasImage(formData, this.destroy$);
      }

      this.serviceInstance.update(this.editId, formData).pipe(takeUntil(this.destroy$)).subscribe({
        next: async (res) => {
          let id = this.editId;
          this.baseService.showSuccessMessage(EDITSUCCESS);

          if (this.formComponent.afterUpdateWork) {
            this.formComponent.afterUpdateWork(id);
          } else if (this.formComponent.afterUpdateWork_async) {
            await this.formComponent.afterUpdateWork_async(id);
          }

          if (this.keepOpenOnNextSave) {
            this.baseService.createFormDisplay.next({ show: true, id: '' });
            this.keepOpenOnNextSave = false;
          } else {
            this.cancelForm();
          }
          this.baseService.refreshTable.next(true);
          this.serviceInstance.channel.postMessage('new-item-added');
        },
        error: (err) => {
          console.log('err', err);
          this.baseService.showErrorMessage(OPERERROR);
        }
      });
    }
  }

  getById(id): void {
    this.serviceInstance.get(id).pipe(takeUntil(this.destroy$)).subscribe((res: SingleResponseModel<T>) => {
      let data;
      if (res?.result) data = res.result;
      else data = res;
      this.form.patchValue(data);
      this.formComponent.afterGetDataWork ? this.formComponent.afterGetDataWork(data) : null;
    });
  }

  /**
   * Open edit dialog/form for an item (called from cards or map view)
   */
  editItem(item: T): void {
    const id = (item as any).id;
    if (id) {
      this.baseService.createFormDisplay.next({ show: true, id });
    }
  }

  /**
   * Open create dialog/form (can be called from any view)
   */
  createItem(): void {
    this.baseService.createFormDisplay.next({ show: true, id: null });
  }

  /**
   * Delete an item (called from cards or map view)
   */
  deleteItem(item: T): void {
    const id = (item as any).id;
    if (!id) return;

    // Use the service's delete method with confirmation
    if (confirm(this.translate.instant('deleteConfirmation'))) {
      this.serviceInstance.delete(id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.baseService.showSuccessMessage(this.translate.instant('deleteSuccess'));
          this.baseService.refreshTable.next(true);
        },
        error: () => {
          this.baseService.showErrorMessage(this.translate.instant('deleteError'));
        }
      });
    }
  }

  /**
   * Get display name for an item in cards view
   * Handles both entity-prefixed fields (sidearaname, sidelatname) and non-prefixed (araname, latname)
   */
  getItemDisplayName(item: any, index: number): string {
    // Try to find name fields in order of preference
    const possibleFields = [
      'name',
      'araname',
      'latname',
      // Entity-prefixed variants (dynamically check all fields ending with araname/latname)
    ];

    // First check standard fields
    for (const field of possibleFields) {
      if (item[field]) return item[field];
    }

    // Then check for any field ending with 'araname' or 'latname' (entity-prefixed)
    const keys = Object.keys(item);
    const aranameField = keys.find(k => k.endsWith('araname'));
    if (aranameField && item[aranameField]) return item[aranameField];

    const latnameField = keys.find(k => k.endsWith('latname'));
    if (latnameField && item[latnameField]) return item[latnameField];

    // Fallback
    return `Item ${index + 1}`;
  }
  //#endregion

  //#region Local Data (same as base-entry)
  private saveLocalData(): void {
    const screen = this.activatedRoute.snapshot.url[0]?.path || '';
    const localFormData: LocalFormData = {
      screen,
      showrestorebtn: true,
      formvalue: this.form.getRawValue()
    };
    let localDataArray: LocalFormData[];
    localDataArray = JSON.parse(localStorage.getItem('localFormData') || '[]');
    let indexToUpdate = localDataArray.findIndex((x) => x.screen == screen);
    if (indexToUpdate > -1) localDataArray[indexToUpdate] = localFormData;
    else localDataArray.push(localFormData);

    localStorage.setItem('localFormData', JSON.stringify(localDataArray));
  }

  private retrieveLocalData(): void {
    const screen = this.activatedRoute.snapshot.url[0]?.path || '';
    const localDataArray: LocalFormData[] = JSON.parse(localStorage.getItem('localFormData') || '[]');
    const localData = localDataArray.find((x) => x.screen == screen);
    if (localData) {
      this.form.patchValue(localData.formvalue);
      this.formComponent.afterGetDataWork ? this.formComponent.afterGetDataWork(localData.formvalue) : null;
    }
  }

  private removeLocalStorage(): void {
    const screen = this.activatedRoute.snapshot.url[0]?.path || '';
    let localDataArray: LocalFormData[];
    localDataArray = JSON.parse(localStorage.getItem('localFormData') || '[]');
    let indexToDelete = localDataArray.findIndex((x) => x.screen == screen);
    if (indexToDelete > -1) localDataArray.splice(indexToDelete, 1);
    localStorage.setItem('localFormData', JSON.stringify(localDataArray));
    this.baseService.saveRetrieveLocalData.next(0);
  }
  //#endregion
}
